import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal, untracked, WritableSignal } from '@angular/core';
import { AppCurrencyService } from 'src/app/app/AppCurrencyService';
import { FilterEditor } from 'src/app/pivot-studio/components/visual-editor/filters/FilterEditor.component';
import { FilterDto } from 'src/app/services/api.services';
import { ProductIcon } from 'src/app/shared/utils';
import { DataModel, DataRelationshipMapping, DataRelationshipMappings, DataSchema, DataSchemaItem, DataSource } from '../dataModel';
import { DataModelResolver } from '../dataModel/DataModelResolver';
import { DataFormatter } from '../formatting/DataFormatter';
import { FilterUtils } from '../utils/FilterUtils';
import { DataQueryVisualDataSource } from '../visuals/data';
import { VisualContext } from '../visuals/VisualContext';
import { VisualCustomField } from '../visuals/VisualCustomField';
import { VisualFieldTargetResolver } from '../visuals/VisualFieldTargetResolver';
import { VisualFilter } from '../visuals/VisualFilter';
import { VisualInteractions } from '../visuals/VisualInteractions';

@Component({
  selector: 'side-panel-filter-list',
  templateUrl: './SidePanelFilterList.component.html',
  styleUrl: './SidePanelFilterList.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {}
})
export class SidePanelFilterListComponent {
  readonly SidePanelFilterListMode = SidePanelFilterListMode;

  readonly currencyService = inject(AppCurrencyService);
  readonly dataModelResolver = inject(DataModelResolver);
  readonly visualDataSource = inject(DataQueryVisualDataSource);
  
  readonly busy = input<boolean>(false);
  readonly loadingInput = input<boolean>(false, { alias: 'loading' });

  readonly loading = computed(() => this.loadingInput() || !this.currencyService.loaded());

  readonly currencies = this.currencyService.currencies;

  readonly mode = input.required<SidePanelFilterListMode>();
  readonly canEdit = input.required<boolean>();
  readonly canModifyFilters = input<boolean>(false);
  readonly editMode = computed(() => this.mode() == SidePanelFilterListMode.Edit);
  readonly viewMode = computed(() => this.mode() == SidePanelFilterListMode.View);

  readonly dataModel = input.required<DataModel>();
  readonly customFields = input<ReadonlyArray<VisualCustomField>>([]);
  readonly fieldResolver = computed(() => new VisualFieldTargetResolver(this.dataModel(), this.customFields()));

  readonly dropTags = computed(() => ['sidePanelFilters']);
  
  readonly filtersInput = model<ReadonlyArray<SidePanelFilterModel>>([], { alias: 'filters' });
  readonly filtersChange = output<ReadonlyArray<SidePanelFilterModel>>();

  readonly filters = signal<ReadonlyArray<SidePanelFilter>>([]);

  readonly visualInteractions = input<VisualInteractions>(null);
  readonly visualInteractions_UpdateRequestFilters = effect(() => {
    let visualInteractions = this.visualInteractions();
    if (!visualInteractions)
      return;

    let filters = this.filters()
      .map(x => x.filter())
      .filter(x => !!x);

    untracked(() => visualInteractions.requestFilters.set(filters));
  });


  readonly datasets = computed<ReadonlyArray<SidePanelDataset>>(
    () => this.dataModel() 
      ? [...this.dataModel().sources.map(s => new SidePanelDataset(s))].sort((a, b) => a.order - b.order)
      : null);

  readonly schemaMap = computed(() => new Map<DataSchema, SidePanelSchema>(
    this.datasets().flatMap(x => x.schemas).map(x => ([x.schema, x]))
  ));

  readonly dragged = signal<SidePanelFilter>(null);
  readonly drop = signal<any>(null);

  readonly showResetPrompt = signal(false);

  readonly resetClick = output();
  readonly editClick = output();

  addFilterClick() {
    if (this.mode() == SidePanelFilterListMode.View) {
      this.editClick.emit();
    }
    else {
      this.addFilter();
    }
  }

  resetFiltersClick() {
    this.showResetPrompt.set(true);
  }
  
  editFiltersClick() {
    this.editClick.emit();
  }

  resetFilters(confirm: boolean, e: Event) {
    e.stopPropagation();

    this.showResetPrompt.set(false);

    if (confirm) 
      this.resetClick.emit();
  }


  constructor() {
    this.currencyService.ensureLoaded();

    effect(() => {
      let [filtersInput, datasets] = [this.filtersInput(), this.datasets()];
     
      untracked(() => {
        let filters: SidePanelFilter[] = [];

        if (datasets && filtersInput?.length) {
          filters = filtersInput.map(x => new SidePanelFilter(this, x));
        }

        this.filters.set(filters);
      });
    });
  }

  emitChange() {
    this.filtersChange.emit([...this.filters().map(x => x.model())]);
  }

  addFilter() {
    this.filters.update(x => [...x, new SidePanelFilter(this)]);
    this.emitChange();
  }

  removeFilter(f: SidePanelFilter) {
    this.filters.update(x => x.filter(x => x != f));
    this.emitChange();
  }

  readonly showDeletePrompt = signal(false);

  clearFilters() {
    if (this.filters().length) {
      this.filters.set([]);
      this.emitChange();
    }

    this.showDeletePrompt.set(false);
  }

  clearFiltersClick() {
    this.showDeletePrompt.set(true);
  }

  onFilterChange(f: SidePanelFilter, newFilter: VisualFilter) {
    f.filter.set(newFilter);
    this.emitChange();
  }

  onDropEnter(drop: any, f: SidePanelFilter, index: number) {
    this.drop.set(drop);
  }
  
  onDropExit(drop: any, f: SidePanelFilter, index: number) {
    this.drop.set(null);
  }

  onDropFilter(f: SidePanelFilter, index: number) {
    this.drop.set(null);

    let filters = [...this.filters()];
    let thisIndex = filters.indexOf(f);
    if (thisIndex == index)
      return;

    filters.splice(thisIndex, 1);
    filters.splice(index, 0, f);

    this.filters.set(filters);
    this.emitChange();
  }
}

export enum SidePanelFilterListMode {
  Edit,
  View
}

export interface SidePanelFilterModel {
  readonly id: number;
  readonly dto?: FilterDto;
  readonly collapsed?: boolean;
  readonly disabled?: boolean;
}

export namespace SidePanelFilterModel {
  let nextId = 1;

  export function generateId() {
    return nextId++;
  }
}

let nextId = 1;

class SidePanelDataset {
  readonly id = nextId++;

  readonly title: string;
  readonly icon: ProductIcon;
  readonly order: number;
  readonly schemas: ReadonlyArray<SidePanelSchema>;

  constructor(readonly source: DataSource) { 
    this.title = source.product.title;
    this.icon  = source.product.icon;
    this.order = source.product.order;

    this.schemas = [...source.schemas.map(s => new SidePanelSchema(this, s))]
      .sort((a, b) => a.schema.itemDisplay.order - b.schema.itemDisplay.order);
  }
}

class SidePanelSchema {
  readonly id = nextId++;

  readonly title: string;
  readonly groups: ReadonlyArray<FilterEditor.Group>;

  constructor(
    readonly dataset: SidePanelDataset,
    readonly schema: DataSchema) {
    this.title = schema.title;

    let sortedGroups = [...schema.itemGroups].sort(sortFunction);

    this.groups = sortedGroups.map(x => {
      let g = new FilterEditor.Group(x.name);
      let sortedItems = [...x.items].sort(sortFunction);
      sortedItems.forEach(i => createItem(i));
      return g;

      function createItem(i: DataSchemaItem, parent?: FilterEditor.Item) {
        let mustHaveAggregation = i.field.effectiveMustHaveAggregation;
        let target = i.createVisualFieldTarget(mustHaveAggregation ? i.field.effectiveDefaultAggregation : null);

        let item = new FilterEditor.Item(g,
          signal(target),
          signal(i.name),
          signal(i.formatLongTitle()),
          signal(i.getEditorIcon()),
          i.children.length > 0,
          parent);

        g.items.push(item);
        i.children.forEach(c => createItem(c, item));

        return item;
      }
    });
  }
}

class SidePanelFilter {
  readonly id: number;

  readonly collapsed: WritableSignal<boolean>;
  setCollapsed(collapsed: boolean) {
    this.collapsed.set(collapsed);
    this.filter()?.collapsed.set(collapsed);
    this.filterList.emitChange();
  }
  
  readonly disabled: WritableSignal<boolean>;
  setDisabled(disabled: boolean) {
    this.disabled.set(disabled);
    this.filter()?.disabled.set(disabled);
    this.filterList.emitChange();
  }

  readonly schema: WritableSignal<SidePanelSchema>;
  setSchema(schema: SidePanelSchema) {
    if (schema == this.schema())
      return;

    this.schema.set(schema);

    let target = schema.groups
      .find(x => x.items.length > 0)
      .items[0]
      .target();

    this.filter.set(new VisualFilter({
      predicate: VisualFilter.Predicate.fromTarget(target),
    }));

    this.filterList.emitChange();
  }

  readonly groups = computed(() => this.schema()?.groups ?? []);

  readonly filter: WritableSignal<VisualFilter>;

  readonly visualContext = computed(() => {
    let [currencies, dataModel, schema, interactions] = [
      this.filterList.currencies,
      this.filterList.dataModel(),
      this.schema(),
      this.filterList.visualInteractions()
    ];

    if (!dataModel || !schema)
      return null;

    return VisualContext.defaultWith({
      debugName: `SidePanelFilter#${this.id}`,
      currencies,
      dataModel,
      schema: schema.schema,
      interactions: interactions,
      fieldResolver: dataModel.customFieldIdentifierLookup.resolver,
    });
  });

  readonly isComplete = computed(() => !this.filter() || !!this.filter().isComplete());
  readonly isValid    = computed(() => !this.filter() || !!this.filter().isValid());

  readonly icon       = computed(() => this.filter()?.icon() ?? 'far fa-grip-lines');
  readonly longTitle  = computed(() => this.filter()?.target()?.longTitle  ?? 'Select Field');
  readonly shortTitle = computed(() => this.filter()?.target()?.shortTitle ?? 'Select Field');

  readonly dragging = signal(false);

  onDragChange(drag: boolean) {
    this.dragging.set(drag);

    if (drag)
      this.filterList.dragged.set(this);
    else
      this.filterList.dragged.set(null);
  }
  

  readonly dto = computed<FilterDto>(() => this.filter()?.createFilterDto());

  readonly model = computed<SidePanelFilterModel>(() => ({
    id: this.id,
    collapsed: this.collapsed(),
    disabled: this.disabled(),
    dto: this.dto()
  }));

  readonly description = computed(() => {
    if (!this.filter() || !this.filter()?.isComplete()) {
      return 'This filter is incomplete';
    }
    else {
      let formatter = new FilterUtils.Formatter(
        this.filterList.fieldResolver(),
        DataFormatter.createDefault({
          currencies: this.filterList.currencies()
        }));

      return formatter.formatDescription(
        this.dto(),
        { capitalizeFirstWord: true });
    }
  });

  constructor(
    readonly filterList: SidePanelFilterListComponent,
    model?: SidePanelFilterModel) {
    this.id = model?.id ?? SidePanelFilterModel.generateId();

    this.collapsed = signal(!!model?.collapsed);
    this.disabled = signal(!!model?.disabled);

    let visualFilter = model?.dto ? new VisualFilter({
      predicate: VisualFilter.Predicate.fromFilterDto(model.dto, filterList.fieldResolver().find),
      collapsed: model.collapsed,
      disabled: model.disabled,
    }) : null;

    let target = visualFilter?.target();
    let schema = target ? filterList.schemaMap().get(target.schema) : null;

    this.schema = signal(schema);
    this.filter = signal(visualFilter);
  }
}

interface Sortable {
  name: string;
  itemDisplay: {
    order: number;
    selectorDisplayName?: string;
  }
};

function sortFunction(a: Sortable, b: Sortable) {
  let d = (a.itemDisplay?.order ?? 0) - (b.itemDisplay?.order ?? 0);
  if (d == 0)
    d = (a.itemDisplay?.selectorDisplayName ?? a.name).localeCompare(b.itemDisplay?.selectorDisplayName ?? b.name);

  return d;
}
